import axios, { AxiosResponse } from 'axios';
import {
  LoginResponse,
  ProfileResponse,
  SubmissionsResponse,
  SubmissionResponse,
  CreateSubmissionResponse,
  MessageResponse,
  User,
  RegisterData,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/leagues';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('leaguePortalToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('leaguePortalToken');
      localStorage.removeItem('leaguePortalUser');
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?expired=true';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (data: RegisterData): Promise<AxiosResponse<MessageResponse>> =>
    api.post('/auth/register', data),
  login: (data: { email: string; password: string }): Promise<AxiosResponse<LoginResponse>> =>
    api.post('/auth/login', data),
  logout: (): Promise<AxiosResponse<void>> =>
    api.post('/auth/logout'),
  verifyEmail: (token: string): Promise<AxiosResponse<MessageResponse>> =>
    api.get(`/auth/verify/${token}`),
  forgotPassword: (email: string): Promise<AxiosResponse<MessageResponse>> =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string): Promise<AxiosResponse<MessageResponse>> =>
    api.post('/auth/reset-password', { token, password }),
};

// Profile API
export const profileApi = {
  get: (): Promise<AxiosResponse<ProfileResponse>> =>
    api.get('/profile'),
  update: (data: Partial<User>): Promise<AxiosResponse<ProfileResponse>> =>
    api.put('/profile', data),
  changePassword: (data: { currentPassword: string; newPassword: string }): Promise<AxiosResponse<MessageResponse>> =>
    api.put('/profile/password', data),
};

// Submission data for create/update
interface SubmissionData {
  leagueName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  requestedDate: string | null;
  season: string;
  notes: string;
  orderDetails: {
    divisions: unknown;
    products: unknown;
  };
  totalTeams: number;
  totalItems: number;
  estimatedTotal: number;
}

// Intake API
export const intakeApi = {
  list: (): Promise<AxiosResponse<SubmissionsResponse>> =>
    api.get('/intake'),
  get: (id: string | number): Promise<AxiosResponse<SubmissionResponse>> =>
    api.get(`/intake/${id}`),
  create: (data: SubmissionData): Promise<AxiosResponse<CreateSubmissionResponse>> =>
    api.post('/intake', data),
  update: (id: string | number, data: SubmissionData): Promise<AxiosResponse<SubmissionResponse>> =>
    api.put(`/intake/${id}`, data),
  delete: (id: string | number): Promise<AxiosResponse<MessageResponse>> =>
    api.delete(`/intake/${id}`),
};

export default api;
